import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Tent,
} from 'lucide-react';
import {
  buildCampInstanceMatrix,
  buildCampSeries,
  formatCampInstanceName,
  computeCampWeekSessionDates,
  formatDateForRegion,
  hasAcademyManagementRole,
  TierFeatureKey,
  applyProgramTemplateToCampInstance,
  buildLessonDefaultsFromProgram,
  type AgeGroup,
  type CampSplitBy,
  type CampInstanceDefaults,
  type Program,
} from '@court-sync/shared';
import { Button, Card, CardContent, Field, FieldLabel, Input, Textarea } from '@/components/ui';
import { LessonCoverImageField } from '@/components/lessons/LessonCoverImageField';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { CampBulkWizardStepTourHelp } from '@/components/tour/CampBulkWizardStepTourHelp';
import { useCampBulkWizardStepTour } from '@/components/tour/useCampBulkWizardStepTour';
import { CAMP_BULK_WIZARD_TOUR_STEP_IDS } from '@/lib/tour-constants';
import { scheduleCampBulkWizardStepTour } from '@/lib/tours/campBulkWizardTour';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { useAuthStore } from '../../../../store/auth';
import { useCurrentAcademy } from '../../../../hooks/useDataStore';
import { useCourts, useCoaches, usePrograms, useLocations } from '../../../../hooks/useDataStore';
import { invalidateLessonsQueries } from '../../../../queries/lessons';
import { useLessonCalendarStore } from '../../../../store/lessonCalendar';
import { useHasMultiLocation } from '../../../../hooks/useHasMultiLocation';
import { useManagementDashboardFeatureAccess } from '../../../../hooks/useFeatureAccess';
import { useAcademySports } from '@/queries/academySports';
import { getAcademySportLabel, resolvePrimaryAcademySportId } from '@/utils/sportPresentation';
import { ageGroupService } from '../../../../services/ageGroup';
import { campService } from '../../../../services/camp';
import { lessonService } from '../../../../services/lesson';
import { uploadLessonImage } from '../../../../services/storage';
import { getCurrencyFromRegion, getCurrencySymbol, formatCurrency, type Currency } from '../../../../utils/currency';
import {
  CampInstanceOverrideTable,
  type CampInstanceRowOverride,
} from './CampInstanceOverrideTable';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const WIZARD_STEPS = [
  { n: 1, label: 'Camp name' },
  { n: 2, label: 'Schedule' },
  { n: 3, label: 'Split' },
  { n: 4, label: 'Preview' },
  { n: 5, label: 'Defaults' },
  { n: 6, label: 'Overrides' },
  { n: 7, label: 'Review' },
] as const;

type WizardStep = (typeof WIZARD_STEPS)[number]['n'];

const DEFAULT_DEFAULTS: CampInstanceDefaults = {
  campDayPrice: 45,
  campFullPrice: 200,
  maxCapacity: 12,
  skillLevel: 'any',
  coachIds: [],
  courtIds: [],
  locationId: null,
};

function buildInstancesPayload(
  matrix: ReturnType<typeof buildCampInstanceMatrix>,
  defaults: CampInstanceDefaults,
  overrides: CampInstanceRowOverride[],
  splitBy: CampSplitBy,
  programsById: Map<string, Program>
) {
  return matrix.map((row, index) => {
    const override = overrides[index] ?? {
      weekIndex: row.weekIndex,
      splitId: row.splitId,
      description: '',
    };
    let skillLevel = override.skillLevel ?? defaults.skillLevel;
    let description = override.description?.trim() || defaults.description?.trim() || '';
    let imagePayload = resolveInstanceImageUrl(defaults, override);

    if (splitBy === 'program' && row.splitId) {
      const program = programsById.get(row.splitId);
      if (program) {
        const templated = applyProgramTemplateToCampInstance(
          {
            skillLevel,
            description,
            imageUrl: 'imageUrl' in override ? override.imageUrl : defaults.imageUrl,
          },
          program
        );
        skillLevel = templated.skillLevel;
        description = templated.description?.trim() || '';
        if (templated.imageUrl?.trim()) {
          imagePayload = { imageUrl: templated.imageUrl.trim() };
        }
      }
    }

    return {
      weekIndex: row.weekIndex,
      splitId: row.splitId,
      campDayPrice: override.campDayPrice ?? defaults.campDayPrice,
      campFullPrice: override.campFullPrice ?? defaults.campFullPrice,
      maxCapacity: override.maxCapacity ?? defaults.maxCapacity,
      skillLevel,
      coachIds: override.coachIds ?? defaults.coachIds,
      courtIds: override.courtIds ?? defaults.courtIds,
      locationId: defaults.locationId ?? null,
      description,
      ...imagePayload,
    };
  });
}

function resolveInstanceImageUrl(
  defaults: CampInstanceDefaults,
  override: CampInstanceRowOverride
): { imageUrl?: string } {
  if ('imageUrl' in override) {
    const url = override.imageUrl?.trim();
    return url ? { imageUrl: url } : {};
  }
  const defaultUrl = defaults.imageUrl?.trim();
  return defaultUrl ? { imageUrl: defaultUrl } : {};
}

export default function CampBulkWizard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currentAcademy } = useCurrentAcademy();
  const academyId = currentAcademy?.id;
  const hasMultiLocation = useHasMultiLocation();
  const { hasAccess: hasProgramsAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.PROGRAMS);
  const { programs } = usePrograms(academyId, { enabled: hasProgramsAccess });
  const { sports: academySports } = useAcademySports(academyId);
  const defaultAcademySportId = resolvePrimaryAcademySportId(academySports);
  const activeAcademySports = useMemo(
    () => academySports.filter((sport) => sport.isActive),
    [academySports]
  );
  const { courts, isLoading: courtsLoading } = useCourts(academyId);
  const { coaches, isLoading: coachesLoading } = useCoaches(academyId);

  const [step, setStep] = useState<WizardStep>(1);
  const [submitting, setSubmitting] = useState(false);
  const { locations } = useLocations(academyId, { includeInactive: true, enabled: hasMultiLocation });
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1, 2, 3, 4, 5]);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('12:00');
  const [weeks, setWeeks] = useState(1);
  const [splitBy, setSplitBy] = useState<CampSplitBy>('none');
  const [splitIds, setSplitIds] = useState<string[]>([]);
  const [academySportId, setAcademySportId] = useState<string | undefined>();
  const [defaults, setDefaults] = useState<CampInstanceDefaults>(DEFAULT_DEFAULTS);
  const [overrides, setOverrides] = useState<CampInstanceRowOverride[]>([]);
  const defaultCoverFileRef = useRef<File | null>(null);
  const [defaultCoverPreview, setDefaultCoverPreview] = useState<string | null>(null);
  const overrideCoverFilesRef = useRef<Map<number, File>>(new Map());
  const [overrideCoverPreviews, setOverrideCoverPreviews] = useState<Record<number, string>>({});
  const [uploadingCover, setUploadingCover] = useState(false);
  const wizardTourScheduleRef = useRef<(() => void) | null>(null);
  const { startTourWithCompletionKey } = usePageTourActions();
  const startStepTour = useCampBulkWizardStepTour(step);

  const academyCurrency: Currency =
    currentAcademy?.currency || getCurrencyFromRegion(currentAcademy?.region);
  const currencySymbol = getCurrencySymbol(academyCurrency);

  useEffect(() => {
    if (defaultAcademySportId && !academySportId) {
      setAcademySportId(defaultAcademySportId);
    }
  }, [defaultAcademySportId, academySportId]);

  useEffect(() => {
    if (!user || !hasAcademyManagementRole(user)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!currentAcademy) return undefined;
    wizardTourScheduleRef.current?.();
    wizardTourScheduleRef.current = scheduleCampBulkWizardStepTour(
      startTourWithCompletionKey,
      step,
      user?.preferences,
    );
    return () => {
      wizardTourScheduleRef.current?.();
      wizardTourScheduleRef.current = null;
    };
  }, [currentAcademy, step, startTourWithCompletionKey, user?.preferences]);

  useEffect(() => {
    if (!hasProgramsAccess && splitBy === 'program') {
      setSplitBy('none');
      setSplitIds([]);
    }
  }, [hasProgramsAccess, splitBy]);

  useEffect(() => {
    if (!academyId) return;
    ageGroupService
      .getAgeGroups(academyId)
      .then((groups) => {
        setAgeGroups(groups.filter((g) => g.isActive).sort((a, b) => a.sortOrder - b.sortOrder));
      })
      .catch(() => setAgeGroups([]));
  }, [academyId]);

  const activePrograms = useMemo(
    () =>
      [...programs]
        .filter((p) => p.isActive)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [programs]
  );

  const activeCoachesForPicker = useMemo(
    () => coaches.filter((c) => c.isActive),
    [coaches]
  );

  const activeCourtsForPicker = useMemo(() => {
    const active = courts.filter((c) => c.isActive);
    if (!hasMultiLocation || locations.length === 0 || !defaults.locationId) {
      return active;
    }
    return active.filter((c) => c.locationId === defaults.locationId);
  }, [courts, hasMultiLocation, locations.length, defaults.locationId]);

  const defaultCoverDisplayUrl = defaultCoverPreview ?? defaults.imageUrl ?? '';

  const handleDefaultCoverFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5MB or smaller');
      return;
    }
    defaultCoverFileRef.current = file;
    setDefaults((d) => ({ ...d, imageUrl: undefined }));
    setDefaultCoverPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  }, []);

  const handleDefaultCoverRemove = useCallback(() => {
    defaultCoverFileRef.current = null;
    setDefaults((d) => ({ ...d, imageUrl: undefined }));
    setDefaultCoverPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!hasMultiLocation || locations.length === 0) return;
    const allowed = new Set(activeCourtsForPicker.map((c) => c.id));
    setDefaults((d) => {
      const nextCourtIds = d.courtIds.filter((id) => allowed.has(id));
      if (nextCourtIds.length === d.courtIds.length) return d;
      return { ...d, courtIds: nextCourtIds };
    });
    setOverrides((prev) => {
      if (prev.length === 0) return prev;
      const next = prev.map((o) => ({
        ...o,
        courtIds: o.courtIds?.filter((id) => allowed.has(id)),
      }));
      const changed = next.some((o, i) => {
        const prevCourtIds = prev[i]?.courtIds;
        if (prevCourtIds === undefined) return false;
        return (o.courtIds?.length ?? 0) !== prevCourtIds.length;
      });
      return changed ? next : prev;
    });
  }, [defaults.locationId, hasMultiLocation, locations.length, activeCourtsForPicker]);

  const matrix = useMemo(
    () => buildCampInstanceMatrix({ weeks, splitBy, splitIds }),
    [weeks, splitBy, splitIds]
  );

  const programsById = useMemo(
    () => new Map(programs.map((p) => [p.id, p])),
    [programs]
  );

  const instanceCount = matrix.length;

  useEffect(() => {
    if (splitBy !== 'program') {
      return;
    }
    setOverrides((prev) =>
      matrix.map((row) => {
        const existing = prev.find(
          (o) => o.weekIndex === row.weekIndex && o.splitId === row.splitId
        );
        const base: CampInstanceRowOverride = {
          weekIndex: row.weekIndex,
          splitId: row.splitId,
          campDayPrice: existing?.campDayPrice,
          campFullPrice: existing?.campFullPrice,
          maxCapacity: existing?.maxCapacity,
          coachIds: existing?.coachIds,
          courtIds: existing?.courtIds,
        };
        const program = row.splitId ? programsById.get(row.splitId) : undefined;
        if (!program) {
          return {
            ...base,
            description: existing?.description ?? '',
            skillLevel: existing?.skillLevel,
            imageUrl: existing?.imageUrl,
          };
        }
        const template = buildLessonDefaultsFromProgram(program);
        return {
          ...base,
          skillLevel: template.skillLevel,
          description: template.description ?? '',
          imageUrl: template.imageUrl ?? '',
        };
      })
    );
    overrideCoverFilesRef.current.clear();
    setOverrideCoverPreviews((prev) => {
      Object.values(prev).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      return {};
    });
  }, [matrix, splitBy, programsById]);

  const getRowCoverDisplayUrl = useCallback(
    (index: number): string => {
      if (overrideCoverPreviews[index]) return overrideCoverPreviews[index]!;
      const override = overrides[index];
      if (override && 'imageUrl' in override) {
        return override.imageUrl ?? '';
      }
      return defaultCoverDisplayUrl;
    },
    [overrideCoverPreviews, overrides, defaultCoverDisplayUrl]
  );

  const hasCustomRowCover = useCallback(
    (index: number): boolean =>
      overrideCoverFilesRef.current.has(index) ||
      (overrides[index] != null && 'imageUrl' in overrides[index]!),
    [overrides]
  );

  const patchOverrideRow = useCallback(
    (index: number, patch: Partial<CampInstanceRowOverride>) => {
      const row = matrix[index]!;
      setOverrides((prev) => {
        const next = [...prev];
        const existing = next[index] ?? {
          weekIndex: row.weekIndex,
          splitId: row.splitId,
          description: '',
        };
        next[index] = { ...existing, ...patch };
        return next;
      });
    },
    [matrix]
  );

  const handleRowCoverFile = useCallback(
    (index: number, file: File) => {
      if (!file.type.startsWith('image/')) {
        toast.error('Please choose an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be 5MB or smaller');
        return;
      }
      overrideCoverFilesRef.current.set(index, file);
      setOverrideCoverPreviews((prev) => {
        const next = { ...prev };
        if (next[index]) URL.revokeObjectURL(next[index]!);
        next[index] = URL.createObjectURL(file);
        return next;
      });
      patchOverrideRow(index, { imageUrl: '' });
    },
    [patchOverrideRow]
  );

  const handleRowCoverRemove = useCallback(
    (index: number) => {
      overrideCoverFilesRef.current.delete(index);
      setOverrideCoverPreviews((prev) => {
        const next = { ...prev };
        if (next[index]) URL.revokeObjectURL(next[index]!);
        delete next[index];
        return next;
      });
      patchOverrideRow(index, { imageUrl: '' });
    },
    [patchOverrideRow]
  );

  const handleRowCoverReset = useCallback((index: number) => {
    overrideCoverFilesRef.current.delete(index);
    setOverrideCoverPreviews((prev) => {
      const next = { ...prev };
      if (next[index]) URL.revokeObjectURL(next[index]!);
      delete next[index];
      return next;
    });
    setOverrides((prev) => {
      const next = [...prev];
      const existing = next[index];
      if (!existing) return prev;
      const { imageUrl: _removed, ...rest } = existing;
      next[index] = rest;
      return next;
    });
  }, []);

  const uploadCampCoverImages = useCallback(
    async (lessonIds: string[]) => {
      if (!academyId) return;
      setUploadingCover(true);
      try {
        for (let i = 0; i < lessonIds.length; i += 1) {
          const lessonId = lessonIds[i]!;
          const overrideFile = overrideCoverFilesRef.current.get(i);
          const override = overrides[i];
          const hasCustom = hasCustomRowCover(i);

          if (overrideFile) {
            const url = await uploadLessonImage(overrideFile, academyId, lessonId);
            await lessonService.updateLesson(lessonId, { imageUrl: url });
            continue;
          }

          if (hasCustom && override && 'imageUrl' in override && !override.imageUrl?.trim()) {
            await lessonService.updateLesson(lessonId, { imageUrl: null });
            continue;
          }

          if (!hasCustom && defaultCoverFileRef.current) {
            const url = await uploadLessonImage(defaultCoverFileRef.current, academyId, lessonId);
            await lessonService.updateLesson(lessonId, { imageUrl: url });
          }
        }
      } finally {
        setUploadingCover(false);
      }
    },
    [academyId, hasCustomRowCover, overrides]
  );

  const computedWeekDates = useMemo(
    () => computeCampWeekSessionDates(startDate, daysOfWeek),
    [startDate, daysOfWeek]
  );

  const campSeriesPreview = useMemo(() => {
    if (computedWeekDates.length === 0 || weeks < 1) return [];
    return buildCampSeries(computedWeekDates, weeks);
  }, [computedWeekDates, weeks]);

  const getVariantLabel = useCallback(
    (splitId: string | null): string => {
      if (!splitId) return '—';
      if (splitBy === 'program') {
        return activePrograms.find((p) => p.id === splitId)?.name ?? splitId;
      }
      if (splitBy === 'age_group') {
        return ageGroups.find((g) => g.id === splitId)?.label ?? splitId;
      }
      return '—';
    },
    [splitBy, activePrograms, ageGroups]
  );

  const toggleDefaultCoach = (coachId: string) => {
    setDefaults((d) => ({
      ...d,
      coachIds: d.coachIds.includes(coachId)
        ? d.coachIds.filter((id) => id !== coachId)
        : [...d.coachIds, coachId],
    }));
  };

  const toggleDefaultCourt = (courtId: string) => {
    setDefaults((d) => ({
      ...d,
      courtIds: d.courtIds.includes(courtId)
        ? d.courtIds.filter((id) => id !== courtId)
        : [...d.courtIds, courtId],
    }));
  };

  const toggleDaySelection = (dayIndex: number) => {
    setDaysOfWeek((current) =>
      current.includes(dayIndex)
        ? current.filter((d) => d !== dayIndex).sort((a, b) => a - b)
        : [...current, dayIndex].sort((a, b) => a - b)
    );
  };

  const toggleSplitId = (id: string) => {
    setSplitIds((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    );
  };

  const validateStep = (s: WizardStep): string | null => {
    if (s === 1) {
      if (!name.trim()) return 'Enter a group name.';
      return null;
    }
    if (s === 2) {
      if (!startDate) return 'Select a start date.';
      if (daysOfWeek.length === 0) return 'Select at least one day of the week.';
      if (!startTime || !endTime) return 'Enter start and end times.';
      const start = new Date(`2000-01-01 ${startTime}`);
      const end = new Date(`2000-01-01 ${endTime}`);
      if (end <= start) return 'End time must be after start time.';
      if (weeks < 1 || weeks > 52) return 'Weeks must be between 1 and 52.';
      if (computedWeekDates.length === 0) return 'Unable to compute camp dates for this schedule.';
      return null;
    }
    if (s === 3) {
      if (splitBy !== 'none' && splitIds.length === 0) {
        return splitBy === 'program'
          ? 'Select at least one program.'
          : 'Select at least one age group.';
      }
      if (splitBy === 'program' && !hasProgramsAccess) {
        return 'Programs require a Professional or Enterprise plan.';
      }
      if (splitBy === 'age_group' && ageGroups.length === 0) {
        return 'Configure age groups in Academy Settings first.';
      }
      return null;
    }
    if (s === 5) {
      if (defaults.campDayPrice <= 0) return 'Day price must be greater than zero.';
      if (defaults.campFullPrice <= 0) return 'Camp weekly price must be greater than zero.';
      if (defaults.maxCapacity !== -1 && defaults.maxCapacity < 1) {
        return 'Capacity must be at least 1 or unlimited.';
      }
      return null;
    }
    if (s === 6) {
      for (let i = 0; i < matrix.length; i += 1) {
        const matrixRow = matrix[i]!;
        const row = overrides[i] ?? {
          weekIndex: matrixRow.weekIndex,
          splitId: matrixRow.splitId,
        };
        const coachIds = row.coachIds ?? defaults.coachIds;
        const courtIds = row.courtIds ?? defaults.courtIds;
        const dayPrice = row.campDayPrice ?? defaults.campDayPrice;
        const fullPrice = row.campFullPrice ?? defaults.campFullPrice;
        const cap = row.maxCapacity ?? defaults.maxCapacity;
        const label = `Week ${matrixRow.weekIndex}${
          matrixRow.splitId ? ` (${getVariantLabel(matrixRow.splitId)})` : ''
        }`;
        if (dayPrice <= 0 || fullPrice <= 0) {
          return `${label}: prices must be greater than zero.`;
        }
        if (cap !== -1 && cap < 1) {
          return `${label}: capacity must be at least 1 or unlimited.`;
        }
        if (coachIds.length === 0) {
          return `${label}: select at least one coach.`;
        }
        if (courtIds.length === 0) {
          return `${label}: select at least one court.`;
        }
      }
      return null;
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) {
      toast.error(err);
      return;
    }
    if (step === 5) {
      setOverrides((prev) => {
        const matchesMatrix =
          prev.length === matrix.length &&
          prev.every(
            (o, i) =>
              o.weekIndex === matrix[i]!.weekIndex && o.splitId === matrix[i]!.splitId
          );
        if (matchesMatrix) return prev;
        return matrix.map((row) => {
          const existing = prev.find(
            (o) => o.weekIndex === row.weekIndex && o.splitId === row.splitId
          );
          return (
            existing ?? {
              weekIndex: row.weekIndex,
              splitId: row.splitId,
              description: '',
            }
          );
        });
      });
    }
    if (step < 7) setStep((step + 1) as WizardStep);
  };

  const goBack = () => {
    setStep((step - 1) as WizardStep);
  };

  const handleCancel = () => {
    navigate('/admin/camps');
  };

  const handleSubmit = async () => {
    if (!academyId) {
      toast.error('No academy selected');
      return;
    }
    for (let s = 1; s <= 6; s += 1) {
      const err = validateStep(s as WizardStep);
      if (err) {
        toast.error(err);
        setStep(s as WizardStep);
        return;
      }
    }

    setSubmitting(true);
    try {
      const instances = buildInstancesPayload(matrix, defaults, overrides, splitBy, programsById);
      const result = await campService.createBulk({
        academyId,
        academySportId: academySportId || defaultAcademySportId,
        name: name.trim(),
        startDate,
        daysOfWeek,
        startTime,
        endTime,
        weeks,
        splitBy,
        splitIds,
        defaults,
        instances,
      });
      await uploadCampCoverImages(result.lessonIds);
      await Promise.all([
        invalidateLessonsQueries(academyId),
        useLessonCalendarStore.getState().refreshAfterLessonMutation(academyId),
      ]);
      toast.success(`Created ${instances.length} camp${instances.length !== 1 ? 's' : ''}`);
      navigate('/admin/camps');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to create camps');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || !hasAcademyManagementRole(user)) return null;

  if (!currentAcademy) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const loadingResources = courtsLoading || coachesLoading;

  return (
    <div className="contents">
      <PageHeader
        title="Create multiple camps"
        titleAccessory={<CampBulkWizardStepTourHelp wizardStep={step} />}
        breadcrumbs={[{ label: 'Camps', href: '/admin/camps' }]}
        help={
          <PageFeatureHelp title="Bulk camp wizard">
            <p>
              Create several camps at once with a shared schedule, optional splits by program or age
              group, and per-instance overrides before you publish the batch.
            </p>
            <div className="border-t border-border pt-3">
              <p className="mb-2 text-xs font-medium text-foreground">Guided tour</p>
              <Button type="button" variant="outline" size="sm" onClick={startStepTour}>
                Tour this step
              </Button>
            </div>
          </PageFeatureHelp>
        }
      />

      {/* Step indicator */}
      <nav aria-label="Wizard progress" className="mb-8 overflow-x-auto">
        <ol className="flex min-w-max gap-1 sm:gap-2">
          {WIZARD_STEPS.map(({ n, label }) => {
            const isActive = step === n;
            const isComplete = step > n;
            return (
              <li key={n} className="flex items-center">
                <span
                  className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium sm:px-3 sm:text-sm ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : isComplete
                        ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/50 dark:text-primary-200'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${
                      isActive
                        ? 'bg-white/20'
                        : isComplete
                          ? 'bg-primary-200 dark:bg-primary-800'
                          : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    {isComplete ? <Check className="h-3 w-3" aria-hidden /> : n}
                  </span>
                  <span className="hidden sm:inline">{label}</span>
                </span>
                {n < WIZARD_STEPS.length ? (
                  <span className="mx-1 h-px w-3 bg-gray-300 dark:bg-gray-600 sm:w-6" aria-hidden />
                ) : null}
              </li>
            );
          })}
        </ol>
      </nav>

      <Card className="border-blue-200 dark:border-blue-800">
        <CardContent className="p-6 sm:p-8">
          {step === 1 && (
            <div
              id={CAMP_BULK_WIZARD_TOUR_STEP_IDS.contentId(1)}
              className="space-y-4 max-w-lg"
            >
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Camp name</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Shared base name and description for this batch. Each camp becomes &quot;{'{name}'} Week N&quot; (with a variant suffix when split).
              </p>
              <Field>
                <FieldLabel htmlFor="campName">Camp name *</FieldLabel>
                <Input
                  id="campName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Summer Juniors 2026"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="campDescription">Default description</FieldLabel>
                <Textarea
                  id="campDescription"
                  rows={4}
                  value={defaults.description ?? ''}
                  onChange={(e) =>
                    setDefaults((d) => ({
                      ...d,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Camp description, what to bring, schedule details..."
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Applied to every camp unless you override it for a specific instance later.
                </p>
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Schedule</h2>
              <div
                id={CAMP_BULK_WIZARD_TOUR_STEP_IDS.SCHEDULE_SPAN}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <Field>
                  <FieldLabel htmlFor="startDate">Start date *</FieldLabel>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Week 1 uses this date; later weeks shift forward by 7 days each.
                  </p>
                </Field>

                <Field>
                  <FieldLabel htmlFor="weeks">Number of weeks *</FieldLabel>
                  <Input
                    id="weeks"
                    type="number"
                    min={1}
                    max={52}
                    value={weeks}
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      setWeeks(Number.isNaN(n) ? 1 : Math.min(52, Math.max(1, n)));
                    }}
                  />
                </Field>
              </div>

              <div id={CAMP_BULK_WIZARD_TOUR_STEP_IDS.SCHEDULE_TIMES_DAYS} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field>
                    <FieldLabel htmlFor="startTime">Start time *</FieldLabel>
                    <Input
                      id="startTime"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="endTime">End time *</FieldLabel>
                    <Input
                      id="endTime"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                  </Field>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Days of week * ({daysOfWeek.length} selected)
                  </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                  {DAY_LABELS.map((label, index) => (
                    <label
                      key={label}
                      className={`flex items-center justify-center p-2 border rounded-md cursor-pointer transition-colors text-sm font-medium ${
                        daysOfWeek.includes(index)
                          ? 'bg-blue-50 border-blue-500 text-blue-800 dark:bg-blue-950 dark:border-blue-400 dark:text-blue-200'
                          : 'bg-white border-gray-300 text-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={daysOfWeek.includes(index)}
                        onChange={() => toggleDaySelection(index)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                {daysOfWeek.length === 0 ? (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                    Select at least one day of the week.
                  </p>
                ) : null}
                {campSeriesPreview.length > 0 ? (
                  <ul className="mt-2 space-y-0.5 text-xs text-blue-700 dark:text-blue-300">
                    {campSeriesPreview.map((week) => {
                      const sorted = [...week.dates].sort();
                      const rangeStart = sorted[0];
                      const rangeEnd = sorted[sorted.length - 1];
                      return (
                        <li key={week.weekIndex}>
                          Week {week.weekIndex}:{' '}
                          {rangeStart && rangeEnd && rangeStart !== rangeEnd ? (
                            <>
                              {formatDateForRegion(rangeStart, currentAcademy.region, {
                                month: 'short',
                                day: 'numeric',
                              })}
                              {' – '}
                              {formatDateForRegion(rangeEnd, currentAcademy.region, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </>
                          ) : rangeStart ? (
                            formatDateForRegion(rangeStart, currentAcademy.region, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          ) : (
                            '—'
                          )}{' '}
                          ({week.dates.length} day{week.dates.length !== 1 ? 's' : ''})
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div id={CAMP_BULK_WIZARD_TOUR_STEP_IDS.contentId(3)} className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Split by variant</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Optionally create separate camp instances per program or age group each week.
                Splitting by program applies each program&apos;s sport, skill level, age group,
                description, and cover image to its camp variants.
              </p>

              <fieldset className="space-y-3">
                <legend className="sr-only">Split method</legend>
                {(
                  [
                    { value: 'none' as const, label: 'No split', desc: 'One camp per week' },
                    {
                      value: 'program' as const,
                      label: 'By program',
                      desc: 'One camp per program per week (Professional+)',
                    },
                    {
                      value: 'age_group' as const,
                      label: 'By age group',
                      desc: 'One camp per age group per week',
                    },
                  ] as const
                )
                  .filter((opt) => opt.value !== 'program' || hasProgramsAccess)
                  .map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                      splitBy === opt.value
                        ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950/40'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="splitBy"
                      className="mt-1 text-blue-600 focus:ring-blue-500"
                      checked={splitBy === opt.value}
                      onChange={() => {
                        setSplitBy(opt.value);
                        setSplitIds([]);
                      }}
                    />
                    <span>
                      <span className="block font-medium text-gray-900 dark:text-white">{opt.label}</span>
                      <span className="block text-sm text-gray-500 dark:text-gray-400">{opt.desc}</span>
                    </span>
                  </label>
                ))}
              </fieldset>

              {splitBy === 'program' && (
                <div>
                  {!hasProgramsAccess ? (
                    <p className="text-sm text-amber-700 dark:text-amber-300 rounded-lg bg-amber-50 dark:bg-amber-950/30 p-3 border border-amber-200 dark:border-amber-800">
                      Programs require a Professional or Enterprise subscription.
                    </p>
                  ) : activePrograms.length === 0 ? (
                    <p className="text-sm text-gray-500">No active programs. Create programs first.</p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-2 space-y-1">
                      {activePrograms.map((p: Program) => (
                        <label
                          key={p.id}
                          className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <input
                            type="checkbox"
                            checked={splitIds.includes(p.id)}
                            onChange={() => toggleSplitId(p.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{p.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {splitBy === 'age_group' && (
                <div>
                  {ageGroups.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No age groups configured. Set them up in Academy Settings.
                    </p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-2 space-y-1">
                      {ageGroups.map((g) => (
                        <label
                          key={g.id}
                          className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <input
                            type="checkbox"
                            checked={splitIds.includes(g.id)}
                            onChange={() => toggleSplitId(g.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{g.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div id={CAMP_BULK_WIZARD_TOUR_STEP_IDS.contentId(4)} className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Preview</h2>
              <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-6 dark:border-blue-800 dark:bg-blue-950/30">
                <p className="text-3xl font-bold text-blue-900 dark:text-blue-100">{instanceCount}</p>
                <p className="text-sm text-blue-800 dark:text-blue-300 mt-1">
                  camp{instanceCount !== 1 ? 's' : ''} will be created
                </p>
                <p className="text-xs text-blue-700/80 dark:text-blue-400 mt-3">
                  {weeks} week{weeks !== 1 ? 's' : ''}
                  {splitBy === 'none'
                    ? ''
                    : ` × ${splitIds.length} variant${splitIds.length !== 1 ? 's' : ''}`}
                </p>
              </div>
              {matrix.length > 0 && matrix.length <= 20 ? (
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 max-h-48 overflow-y-auto">
                  {matrix.map((row) => (
                    <li key={`${row.weekIndex}-${row.splitId ?? 'none'}`}>
                      {formatCampInstanceName(
                        name.trim() || 'Camp',
                        row.weekIndex,
                        row.splitId ? getVariantLabel(row.splitId) : undefined
                      )}
                    </li>
                  ))}
                </ul>
              ) : matrix.length > 20 ? (
                <p className="text-sm text-gray-500">Too many instances to list ({instanceCount} total).</p>
              ) : null}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6">
              <div id={CAMP_BULK_WIZARD_TOUR_STEP_IDS.DEFAULTS_SHARED} className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Default settings</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Default pricing, capacity, location, coaches, courts, and cover image for these
                  camps. Override any instance in the next step.
                </p>

                {loadingResources ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
                    {activeAcademySports.length > 1 ? (
                      <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Sport *
                        </label>
                        <select
                          className="input w-full"
                          value={academySportId ?? ''}
                          onChange={(e) => setAcademySportId(e.target.value || undefined)}
                        >
                          {activeAcademySports.map((sport) => (
                            <option key={sport.id} value={sport.id}>
                              {getAcademySportLabel(sport)}
                              {sport.isPrimary ? ' (primary)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}

                    <Field>
                      <FieldLabel htmlFor="campDayPrice">Price per day ({currencySymbol}) *</FieldLabel>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        {currencySymbol}
                      </span>
                      <Input
                        id="campDayPrice"
                        type="number"
                        step="0.01"
                        className="pl-12"
                        value={defaults.campDayPrice}
                        onWheel={(e) => e.currentTarget.blur()}
                        onChange={(e) => {
                          const n = parseFloat(e.target.value);
                          setDefaults((d) => ({
                            ...d,
                            campDayPrice: Number.isNaN(n) ? 0 : n,
                          }));
                        }}
                      />
                    </div>
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="campFullPrice">Camp weekly price ({currencySymbol}) *</FieldLabel>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        {currencySymbol}
                      </span>
                      <Input
                        id="campFullPrice"
                        type="number"
                        step="0.01"
                        className="pl-12"
                        value={defaults.campFullPrice}
                        onWheel={(e) => e.currentTarget.blur()}
                        onChange={(e) => {
                          const n = parseFloat(e.target.value);
                          setDefaults((d) => ({
                            ...d,
                            campFullPrice: Number.isNaN(n) ? 0 : n,
                          }));
                        }}
                      />
                    </div>
                  </Field>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Maximum capacity *
                      </label>
                      <label className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          checked={defaults.maxCapacity === -1}
                          onChange={(e) => {
                            setDefaults((d) => ({
                              ...d,
                              maxCapacity: e.target.checked ? -1 : 12,
                            }));
                          }}
                        />
                        <span className="text-gray-700 dark:text-gray-300">No limit</span>
                      </label>
                    </div>
                    {defaults.maxCapacity !== -1 ? (
                      <Input
                        type="number"
                        min={1}
                        value={defaults.maxCapacity}
                        onWheel={(e) => e.currentTarget.blur()}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          setDefaults((d) => ({
                            ...d,
                            maxCapacity: Number.isNaN(n) ? 1 : n,
                          }));
                        }}
                      />
                    ) : (
                      <div className="p-2 border rounded bg-gray-50 text-gray-500 text-sm italic dark:bg-gray-800 dark:border-gray-700">
                        Unlimited students allowed
                      </div>
                    )}
                  </div>
                  </div>
                )}
              </div>

              {!loadingResources ? (
                <div
                  id={CAMP_BULK_WIZARD_TOUR_STEP_IDS.DEFAULTS_OPTIONAL}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Skill level
                    </label>
                    <select
                      className="input w-full"
                      value={defaults.skillLevel}
                      onChange={(e) =>
                        setDefaults((d) => ({
                          ...d,
                          skillLevel: e.target.value as CampInstanceDefaults['skillLevel'],
                        }))
                      }
                    >
                      <option value="any">Any Level</option>
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>

                  {hasMultiLocation && locations.filter((l) => l.isActive).length > 0 ? (
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Location
                      </label>
                      <select
                        className="input w-full"
                        value={defaults.locationId ?? ''}
                        onChange={(e) =>
                          setDefaults((d) => ({
                            ...d,
                            locationId: e.target.value || null,
                          }))
                        }
                      >
                        <option value="">All locations / not specified</option>
                        {locations
                          .filter((l) => l.isActive)
                          .sort((a, b) => a.sortOrder - b.sortOrder)
                          .map((loc) => (
                            <option key={loc.id} value={loc.id}>
                              {loc.name}
                            </option>
                          ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Applies to all camp instances. Courts in the next step are filtered by this
                        location.
                      </p>
                    </div>
                  ) : null}

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Default courts ({defaults.courtIds.length} selected)
                    </label>
                    <div className="max-h-32 overflow-y-auto rounded-md border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-800">
                      {activeCourtsForPicker.length === 0 ? (
                        <p className="text-sm text-gray-500">No active courts available</p>
                      ) : (
                        activeCourtsForPicker.map((court) => (
                          <label
                            key={court.id}
                            className="flex items-center space-x-2 rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={defaults.courtIds.includes(court.id)}
                              onChange={() => toggleDefaultCourt(court.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{court.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Applied to every camp unless overridden per instance.
                    </p>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Default coaches ({defaults.coachIds.length} selected)
                    </label>
                    <div className="max-h-32 overflow-y-auto rounded-md border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-800">
                      {activeCoachesForPicker.length === 0 ? (
                        <p className="text-sm text-gray-500">No active coaches available</p>
                      ) : (
                        activeCoachesForPicker.map((coach) => (
                          <label
                            key={coach.id}
                            className="flex items-center space-x-2 rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={defaults.coachIds.includes(coach.id)}
                              onChange={() => toggleDefaultCoach(coach.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {coach.firstName} {coach.lastName}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Applied to every camp unless overridden per instance.
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <LessonCoverImageField
                      displayUrl={defaultCoverDisplayUrl}
                      uploading={uploadingCover}
                      disabled={submitting}
                      inputId="camp-group-default-cover-upload"
                      onFileSelect={handleDefaultCoverFile}
                      onRemove={handleDefaultCoverRemove}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Applied to every camp instance unless overridden in the next step.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {step === 6 && (
            <div id={CAMP_BULK_WIZARD_TOUR_STEP_IDS.contentId(6)} className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Per-instance settings</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Override coaches, courts, or cover image for individual camp instances. Expand a row
                to edit. Pricing, description, coaches, courts, and cover image default from earlier
                steps unless overridden here.
              </p>
              {loadingResources ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <CampInstanceOverrideTable
                  matrix={matrix}
                  defaults={defaults}
                  getVariantLabel={getVariantLabel}
                  currencySymbol={currencySymbol}
                  overrides={overrides}
                  onChange={setOverrides}
                  courts={courts}
                  coaches={coaches}
                  defaultLocationId={defaults.locationId}
                  defaultCoverDisplayUrl={defaultCoverDisplayUrl}
                  getRowCoverDisplayUrl={getRowCoverDisplayUrl}
                  hasCustomRowCover={hasCustomRowCover}
                  onRowCoverFile={handleRowCoverFile}
                  onRowCoverRemove={handleRowCoverRemove}
                  onRowCoverReset={handleRowCoverReset}
                  uploadingCover={uploadingCover || submitting}
                />
              )}
            </div>
          )}

          {step === 7 && (
            <div id={CAMP_BULK_WIZARD_TOUR_STEP_IDS.contentId(7)} className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Review and create</h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Camp name</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{name.trim()}</dd>
                </div>
                {defaults.description?.trim() ? (
                  <div className="sm:col-span-2">
                    <dt className="text-gray-500 dark:text-gray-400">Default description</dt>
                    <dd className="font-medium text-gray-900 dark:text-white whitespace-pre-wrap">
                      {defaults.description.trim()}
                    </dd>
                  </div>
                ) : null}
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Camps</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{instanceCount}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Schedule</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">
                    {weeks} week{weeks !== 1 ? 's' : ''} ·{' '}
                    {daysOfWeek.map((d) => DAY_LABELS[d]).join(', ')} · {startTime}–{endTime}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Start date</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{startDate}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Split</dt>
                  <dd className="font-medium text-gray-900 dark:text-white capitalize">
                    {splitBy === 'none'
                      ? 'None'
                      : splitBy === 'program'
                        ? `Program (${splitIds.length})`
                        : `Age group (${splitIds.length})`}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Default pricing</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(defaults.campDayPrice, academyCurrency)} / day ·{' '}
                    {formatCurrency(defaults.campFullPrice, academyCurrency)} weekly
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Capacity</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">
                    {defaults.maxCapacity === -1 ? 'Unlimited' : defaults.maxCapacity}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Default coaches & courts</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">
                    {defaults.coachIds.length} coach{defaults.coachIds.length !== 1 ? 'es' : ''} ·{' '}
                    {defaults.courtIds.length} court{defaults.courtIds.length !== 1 ? 's' : ''}
                    {hasMultiLocation && defaults.locationId ? ' · shared location' : null}
                  </dd>
                </div>
              </dl>
              <div className="rounded-lg border border-blue-200 bg-blue-50/30 p-4 dark:border-blue-800 dark:bg-blue-950/20 flex items-start gap-3">
                <Tent className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" aria-hidden />
                <p className="text-sm text-blue-900 dark:text-blue-200">
                  Creating {instanceCount} camp{instanceCount !== 1 ? 's' : ''} in one batch. You can edit or deactivate individual camps later from Camp Management.
                </p>
              </div>
            </div>
          )}

          {/* Footer navigation */}
          <div className="flex justify-between gap-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-2">
              {step > 1 ? (
                <Button type="button" variant="outline" onClick={goBack}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              ) : null}
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
            {step < 7 ? (
              <Button type="button" onClick={goNext}>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button type="button" onClick={handleSubmit} isLoading={submitting}>
                Create {instanceCount} camp{instanceCount !== 1 ? 's' : ''}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
